import {
  createDimension,
  DIM_GROUP,
  DIM_TYPE,
} from '../../three/core/dimension/dimensionTypes';

/**
 * Dữ liệu DIM đưa vào payload lưu thiết kế (JSON thuần).
 * Bỏ cờ `orphan` vì nó được tính lại khi load theo module hiện có.
 */
export const buildDimensionPayload = items =>
  (Array.isArray(items) ? items : []).map(({ orphan, ...rest }) => rest);

/**
 * Đọc `dimensions` từ thiết kế đã lưu. Khoan dung với dữ liệu cũ:
 * thiếu field -> [], phần tử hỏng bị bỏ, field thiếu được điền mặc định.
 */
export const parseDimensions = raw => {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter(
      item =>
        item &&
        typeof item === 'object' &&
        typeof item.id === 'string' &&
        Array.isArray(item.anchors) &&
        item.anchors.every(a => a && typeof a.instanceId === 'string')
    )
    .map(item =>
      createDimension({
        ...item,
        type: item.type ?? DIM_TYPE.LINEAR,
        orphan: false,
      })
    );
};

const isVec3 = v =>
  Array.isArray(v) && v.length === 3 && v.every(n => Number.isFinite(n));

/** Bộ DIM của một view đưa vào payload (chỉ giữ các trường biết). */
const cleanViewDim = dim => {
  if (!dim || typeof dim !== 'object') return undefined;
  const layers = {};
  Object.values(DIM_GROUP).forEach(g => {
    layers[g] = dim.layers?.[g] !== false;
  });
  return {
    layers,
    preset: typeof dim.preset === 'string' ? dim.preset : 'custom',
    hiddenIds: Array.isArray(dim.hiddenIds)
      ? dim.hiddenIds.filter(id => typeof id === 'string')
      : [],
  };
};

/** Góc nhìn (camera + bộ DIM hiện) đưa vào payload lưu thiết kế. */
export const buildViewsPayload = views =>
  (Array.isArray(views) ? views : []).map(
    ({ id, name, position, target, dim }) => {
      const cleaned = cleanViewDim(dim);
      return {
        id,
        name,
        position,
        target,
        ...(cleaned ? { dim: cleaned } : {}),
      };
    }
  );

/**
 * Đọc `dimensionViews` từ thiết kế đã lưu. Khoan dung: thiếu -> [], phần tử hỏng
 * (toạ độ không phải 3 số) bị bỏ, thiếu tên được đặt tên mặc định. View lưu từ
 * bản cũ không có `dim` (chỉ camera) vẫn đọc được.
 */
export const parseViews = raw => {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter(v => v && isVec3(v.position) && isVec3(v.target))
    .map((v, i) => {
      const dim = cleanViewDim(v.dim);
      return {
        id: typeof v.id === 'string' && v.id ? v.id : `view_loaded_${i}`,
        name:
          typeof v.name === 'string' && v.name.trim()
            ? v.name
            : `View ${i + 1}`,
        position: v.position,
        target: v.target,
        ...(dim ? { dim } : {}),
      };
    });
};
